import { useState, useMemo, useCallback } from 'react';
import {
  Heading,
  Button,
  Combobox,
  SelectField,
  toaster,
} from 'evergreen-ui';
import Ribbon from './Ribbon';
import useStore from './useStore';
import useLoadAllData from './useLoadAllData';
import { supabase } from './utils';
import './CheckIn.css';

const CheckIn = () => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const tourneys = useStore((state) => state.tourneys);
  const categories = useStore((state) => state.categories);
  const players = useStore((state) => state.players);
  const playerCategories = useStore((state) => state.playerCategories);

  const { loadAllData } = useLoadAllData();

  const currentTourney = useMemo(() => tourneys[0], [tourneys]);

  const tourneyCategories = useMemo(() => {
    if (!currentTourney) return [];
    return categories.filter(
      (cat) =>
        Number(cat.tourneyId) === currentTourney.id && cat.checkinOpen === true
    );
  }, [categories, currentTourney]);

  // Total assigned to this category (seed = null or not null)
  const totalAssignedCount = useMemo(() => {
    if (!selectedCategoryId) return 0;
    return playerCategories.filter(
      (pc) => pc.categoryId === selectedCategoryId
    ).length;
  }, [playerCategories, selectedCategoryId]);

  // Checked-in players (seed != null)
  const checkedInPlayersForCategory = useMemo(() => {
    if (!selectedCategoryId) return [];
    return playerCategories
      .filter((pc) => pc.categoryId === selectedCategoryId && pc.seed !== null)
      .map((pc) => {
        const player = players.find((p) => p.id === pc.playerId);
        return {
          ...pc,
          playerName: player?.name || 'Unknown Player',
        };
      })
      .sort((a, b) => {
        if (a.seed !== null && b.seed !== null) return a.seed - b.seed;
        if (a.seed !== null) return -1;
        if (b.seed !== null) return 1;
        return a.playerName.localeCompare(b.playerName);
      });
  }, [playerCategories, selectedCategoryId, players]);

  // Available players: assigned to category (have row in player_categories) but NOT checked in (seed = null)
  const availablePlayers = useMemo(() => {
    if (!selectedCategoryId) return [];

    // Get players assigned to this category with no seed
    const assignedNotCheckedIn = playerCategories
      .filter((pc) => pc.categoryId === selectedCategoryId && pc.seed === null)
      .map((pc) => pc.playerId);

    return players.filter((p) => assignedNotCheckedIn.includes(p.id));
  }, [selectedCategoryId, playerCategories, players]);

  const handleCheckIn = useCallback(async () => {
    if (!selectedCategoryId || !selectedPlayerId) {
      toaster.warning('Please select both a category and a player');
      return;
    }

    setCheckingIn(true);

    try {
      // Generate truly random seed (not limited to 1-N)
      const randomSeed = Math.floor(Math.random() * 10000) + 1;

      // UPDATE existing row (player was pre-assigned with seed = null)
      const { error } = await supabase
        .from('player_categories')
        .update({ seed: randomSeed })
        .eq('playerId', selectedPlayerId)
        .eq('categoryId', selectedCategoryId);

      if (error) {
        toaster.danger('Failed to check in player');
        console.error('Check-in error:', error);
      } else {
        toaster.success(`Player checked in with seed ${randomSeed}`);
        setSelectedPlayerId(null);
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred while checking in');
      console.error('Check-in error:', err);
    }

    setCheckingIn(false);
  }, [selectedCategoryId, selectedPlayerId, loadAllData]);

  return (
    <>
      <div className='home-page-image-container'>
        <img src='/logo.png' height='120px' alt='Pickleball Tournament Logo' />
      </div>
      {currentTourney && (
        <Ribbon text={currentTourney.name} showBackButton={true} />
      )}

      <div className='checkin-container'>
        <div className='checkin-header'>
          <Heading size={700} marginBottom={20}>
            Check-In
          </Heading>
        </div>

        <div className='checkin-category-select'>
          {tourneyCategories.length === 0 ? (
            <div className='checkin-no-categories'>
              No categories are currently open for check-in.
            </div>
          ) : (
            <SelectField
              label='Category'
              value={selectedCategoryId?.toString() || ''}
              onChange={(e) =>
                setSelectedCategoryId(
                  e.target.value ? Number(e.target.value) : null
                )
              }
            >
              <option value=''>Select a category</option>
              {tourneyCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </SelectField>
          )}
        </div>

        {selectedCategoryId && (
          <>
            <div className='checkin-card'>
              <div className='checkin-card-title'>Check-in a Player</div>
              {availablePlayers.length === 0 ? (
                <div className='checkin-no-players-message'>
                  {totalAssignedCount === 0
                    ? 'No players have been assigned to this category yet. Please ask an admin to assign players first.'
                    : 'All assigned players have already checked in.'}
                </div>
              ) : (
                <>
                  <div className='checkin-form'>
                    <div className='checkin-player-select'>
                      <Combobox
                        items={availablePlayers}
                        itemToString={(item) => (item ? item.name : '')}
                        onChange={(selected) =>
                          setSelectedPlayerId(selected?.id || null)
                        }
                        placeholder='Search player...'
                        selectedItem={
                          availablePlayers.find((p) => p.id === selectedPlayerId) ||
                          null
                        }
                        openOnFocus
                        autocompleteProps={{
                          title: 'Players',
                        }}
                        width="100%"
                      />
                    </div>
                  </div>
                  <div className='checkin-submit-button'>
                    <Button
                      appearance='primary'
                      onClick={handleCheckIn}
                      isLoading={checkingIn}
                      disabled={!selectedPlayerId}
                      marginTop={10}
                    >
                      Check in with random seed
                    </Button>
                  </div>
                </>
              )}
            </div>

            <div className='checkin-card'>
              <div className='checkin-list-header'>
                <div className='checkin-card-title'>Checked-in Players</div>
                <div className='checkin-count'>
                  {checkedInPlayersForCategory.length}/{totalAssignedCount}
                </div>
              </div>

              {checkedInPlayersForCategory.length === 0 ? (
                <div className='checkin-empty-state'>
                  <div className='checkin-empty-state-icon'>📋</div>
                  <div>No players checked in yet</div>
                </div>
              ) : (
                <ul className='checkin-list'>
                  {checkedInPlayersForCategory.map((pc, index) => (
                    <li key={pc.id} className='checkin-list-item'>
                      <div className='checkin-player-info'>
                        <span className='checkin-player-number'>
                          {index + 1}.
                        </span>
                        <span className='checkin-player-name'>
                          {pc.playerName}
                        </span>
                      </div>
                      <div className='checkin-seed-container'>
                        <span className='checkin-seed'>
                          Seed: {pc.seed ?? 'N/A'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CheckIn;
